import React, { useState, useEffect } from 'react';
import { toast } from '../../components/toast/CustomToast'
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import api from '../../utils/api';

const CreateUserModal = ({ isOpen, onClose, companyId, onUserCreated }) => {
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '', // Será ignorado pelo backend
        is_admin: false
    });
    const [loading, setLoading] = useState(false);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newData = { ...formData, [name]: type === 'checkbox' ? checked : value };
        setFormData(newData);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.username.trim()) {
            toast.error('Por favor, informe o nome de usuário');
            return;
        }

        if (!formData.email.trim()) {
            toast.error('Por favor, informe o email');
            return;
        }

        setLoading(true);
        try {
            await api.post(`/company-users/${companyId}`, formData);
            toast.success('Usuário criado! Email de ativação enviado para ' + formData.email);
            setFormData({ username: '', email: '', password: '', is_admin: false });
            onUserCreated();
            onClose();
        } catch (error) {
            let errorMessage = 'Erro ao criar usuário';
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.message) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} title="Novo Usuário" onClose={onClose}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Usuário
                    </label>
                    <input
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="Nome de usuário"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email
                    </label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="seu@email.com"
                    />
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <div className="flex gap-2">
                        <span className="material-icons text-blue-600 dark:text-blue-400" style={{fontSize: '18px'}}>info</span>
                        <div className="text-xs text-blue-900 dark:text-blue-200">
                            <p className="font-medium mb-1">Fluxo de ativação:</p>
                            <p>Uma senha temporária será gerada e um email com link de reset será enviado para o usuário. Ele será forçado a criar sua própria senha.</p>
                        </div>
                    </div>
                </div>

                <label className="flex items-center">
                    <input
                        type="checkbox"
                        name="is_admin"
                        checked={formData.is_admin}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-500 rounded focus:ring-2"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        É administrador
                    </span>
                </label>

                <div className="flex gap-3 pt-4">
                    <Button variant="secondary" onClick={onClose} type="button">
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        loading={loading}
                        className="flex-1"
                    >
                        <span className="material-icons sm">add</span>
                        Criar Usuário
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

const EmailChangeConfirmMessage = ({ optionsRef, oldEmail, newEmail }) => {
    const [sendReset, setSendReset] = useState(optionsRef.current.sendReset);

    const handleChange = (e) => {
        optionsRef.current.sendReset = e.target.checked;
        setSendReset(e.target.checked);
    };

    return (
        <span>
            Confirma a alteração do email de <strong>{oldEmail}</strong> para <strong>{newEmail}</strong>?
            <label className="flex items-center gap-2 mt-2 text-xs font-normal cursor-pointer">
                <input
                    type="checkbox"
                    checked={sendReset}
                    onChange={handleChange}
                    className="w-3.5 h-3.5 text-blue-500 rounded focus:ring-2"
                />
                Enviar e-mail de reset de senha
            </label>
        </span>
    );
};

const EditUserModal = ({ isOpen, onClose, companyId, user, onUserUpdated, onResetPassword }) => {
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        is_admin: false
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user) {
            setFormData({
                username: user.username,
                email: user.email,
                is_admin: user.is_admin
            });
        }
    }, [user]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    };

    const submitUpdate = async (sendResetEmail) => {
        setLoading(true);
        try {
            await api.put(`/company-users/${companyId}/${user.id}`, formData);
            toast.success('Usuário atualizado com sucesso!');
            if (sendResetEmail) {
                onResetPassword(user.id);
            }
            onUserUpdated();
            onClose();
        } catch (error) {
            let errorMessage = 'Erro ao atualizar usuário';
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.message) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.username.trim()) {
            toast.error('Por favor, informe o nome de usuário');
            return;
        }

        if (!formData.email.trim()) {
            toast.error('Por favor, informe o email');
            return;
        }

        const newEmail = formData.email.trim().toLowerCase();
        const emailChanged = newEmail !== user.email.toLowerCase();

        if (!emailChanged) {
            submitUpdate(false);
            return;
        }

        const optionsRef = { current: { sendReset: true } };
        toast.confirm(
            <EmailChangeConfirmMessage optionsRef={optionsRef} oldEmail={user.email} newEmail={newEmail} />,
            () => submitUpdate(optionsRef.current.sendReset),
            () => {}
        );
    };

    if (!user) return null;

    return (
        <Modal isOpen={isOpen} title="Editar Usuário" onClose={onClose}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Usuário
                    </label>
                    <input
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="Nome de usuário"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email
                    </label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="seu@email.com"
                    />
                </div>

                <label className="flex items-center">
                    <input
                        type="checkbox"
                        name="is_admin"
                        checked={formData.is_admin}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-blue-500 rounded focus:ring-2"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        É administrador
                    </span>
                </label>

                <div className="flex gap-3 pt-4">
                    <Button variant="secondary" onClick={onClose} type="button">
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        loading={loading}
                        className="flex-1"
                    >
                        <span className="material-icons sm">save</span>
                        Salvar Alterações
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

const CompanyUsersPage = ({ companyId, userInfo }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const loadUsers = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/company-users/${companyId}`);
            const data = response.data;
            // Ensure data is an array
            setUsers(Array.isArray(data) ? data : []);
        } catch (error) {
            toast.error(`Erro ao carregar usuários: ${error.message}`);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUsers();
    }, [companyId]);

    const handleBlockUser = async (userId) => {
        try {
            await api.patch(`/company-users/${companyId}/${userId}/block`, {});
            toast.success('Usuário atualizado com sucesso!');
            loadUsers();
        } catch (error) {
            toast.error(`Erro: ${error.message}`);
        }
    };

    const handleResetPassword = async (userId) => {
        try {
            await api.post(`/company-users/${companyId}/${userId}/reset-password`, {});
            toast.success('Email de reset enviado com sucesso!');
        } catch (error) {
            toast.error(`Erro: ${error.message}`);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <span className="material-icons text-4xl text-blue-500 animate-spin">refresh</span>
                    <p className="text-gray-600 dark:text-gray-400 mt-2">Carregando usuários...</p>
                </div>
            </div>
        );
    }

    const filteredUsers = (Array.isArray(users) ? users : []).filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Gerenciar Usuários</h2>
                <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                    <span className="material-icons sm">add</span>
                    Novo Usuário
                </Button>
            </div>

            {users.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-6 py-4">
                    <div className="flex items-center gap-3">
                        <span className="material-icons text-gray-400">search</span>
                        <input
                            type="text"
                            placeholder="Pesquisar por usuário ou email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="flex-1 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none text-sm"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                            >
                                <span className="material-icons sm">close</span>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {filteredUsers.length === 0 ? (
                <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <span className="material-icons text-4xl text-gray-300 block mb-2">people</span>
                    <p className="text-gray-500 dark:text-gray-400">
                        {searchTerm ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado ainda'}
                    </p>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                            <tr>
                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Usuário</th>
                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Email</th>
                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Papel</th>
                                <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition border-b border-gray-200 dark:border-gray-600">
                                    <td className="px-6 py-4 text-sm text-gray-800 dark:text-gray-200">{user.username}</td>
                                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{user.email}</td>
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                            user.is_active
                                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                                                : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                                        }`}>
                                            {user.is_active ? 'Ativo' : 'Bloqueado'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        {user.is_admin && (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                                                Administrador
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex gap-2 justify-center">
                                            <button
                                                onClick={() => setEditingUser(user)}
                                                className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                                                title="Editar usuário"
                                            >
                                                <span className="material-icons sm">edit</span>
                                            </button>
                                            <button
                                                onClick={() => handleResetPassword(user.id)}
                                                className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition"
                                                title="Enviar link de reset de senha"
                                            >
                                                <span className="material-icons sm">vpn_key</span>
                                            </button>
                                            <button
                                                onClick={() => handleBlockUser(user.id)}
                                                disabled={user.is_admin}
                                                className={`p-1.5 rounded-lg transition ${
                                                    user.is_admin
                                                        ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                                                        : user.is_active
                                                            ? 'text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                                                            : 'text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30'
                                                }`}
                                                title={
                                                    user.is_admin
                                                        ? 'Não é possível bloquear administradores'
                                                        : user.is_active
                                                            ? 'Bloquear usuário'
                                                            : 'Desbloquear usuário'
                                                }
                                            >
                                                <span className="material-icons sm">{user.is_active ? 'block' : 'check_circle'}</span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <CreateUserModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                companyId={companyId}
                onUserCreated={loadUsers}
            />

            <EditUserModal
                isOpen={!!editingUser}
                onClose={() => setEditingUser(null)}
                companyId={companyId}
                user={editingUser}
                onUserUpdated={loadUsers}
                onResetPassword={handleResetPassword}
            />
        </div>
    );
};

export default CompanyUsersPage;
